/**
 * sectionChecklist — the shared section timeline.
 *
 * One component for the two surfaces that list a set of discovery sections with
 * their state: the `/discovery` path-detail drawer and the topic-detail panel.
 * Both render the same row — status marker on a connector line, name, chevron,
 * collapsed description — and differ only in the marker's colour pair, so the
 * topic surface can carry the topic's identity hue where the path surface
 * carries its green.
 *
 * Rows are expanded by default — the descriptions are what makes the list worth
 * reading — and the whole text block is the toggle, so collapsing is a generous
 * target rather than a hunt for the title.
 *
 * @module components/sectionChecklist
 */

import { renderSectionStatusIcon } from './sectionStatusIcon.js';
import { escapeHtml } from '../core/utils.js';

const CHEVRON = `
  <svg class="section-checklist__chevron" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

/**
 * Render the section timeline.
 *
 * @param {Array<{key?: string, name: string, description?: string, status?: string}>} items
 *   Rows in display order. `status` is the `sectionStatusIcon` vocabulary
 *   (`complete` / `in_progress` / anything else → not started).
 * @param {Object} [options]
 * @param {string} [options.color] - Marker colour for resolved/in-progress rows
 * @param {string} [options.emptyColor] - Marker colour for not-started rows
 * @returns {string} HTML string (a `<ul>`). Rows render expanded.
 */
export function renderSectionChecklist(items, { color, emptyColor } = {}) {
  const rows = (items || []).map(item => {
    const status = item.status || 'not_started';
    // The description is a <span>, not a <p>, so it can live INSIDE the button:
    // a button may only contain phrasing content, and having the whole text
    // block be the control is the point.
    const descriptionHtml = item.description
      ? `<span class="section-checklist__description">${escapeHtml(item.description)}</span>`
      : '';
    return `
      <li class="section-checklist__row"
          data-status="${escapeHtml(status)}"${item.key ? ` data-section-key="${escapeHtml(item.key)}"` : ''}>
        <span class="section-checklist__icon" aria-hidden="true">
          ${renderSectionStatusIcon(status, { color, emptyColor })}
        </span>
        <button type="button" class="section-checklist__toggle" aria-expanded="true">
          <span class="section-checklist__head">
            <span class="section-checklist__name">${escapeHtml(item.name)}</span>
            ${CHEVRON}
          </span>
          ${descriptionHtml}
        </button>
      </li>
    `;
  }).join('');

  return `<ul class="section-checklist">${rows}</ul>`;
}

/**
 * Wire the chevron toggles. Delegated from a container that survives
 * re-renders, so it can be called once per mount regardless of when the rows
 * arrive. Idempotent per element — the caller owns the container's lifetime.
 *
 * @param {HTMLElement} root - Element containing (or that will contain) the list
 * @returns {() => void} Cleanup that removes the listener
 */
export function bindSectionChecklist(root) {
  if (!root) return () => {};
  const onClick = (e) => {
    const toggle = e.target.closest('.section-checklist__toggle');
    if (!toggle || !root.contains(toggle)) return;
    const row = toggle.closest('.section-checklist__row');
    if (!row) return;
    const nextCollapsed = !row.classList.contains('section-checklist__row--collapsed');
    row.classList.toggle('section-checklist__row--collapsed', nextCollapsed);
    toggle.setAttribute('aria-expanded', String(!nextCollapsed));
  };
  root.addEventListener('click', onClick);
  return () => root.removeEventListener('click', onClick);
}

/** Tiers that mean the user finished everything this section asked of them. */
const RESOLVED_TIERS = new Set(['l2_complete', 'l3_active', 'l3_complete', 'l2_withheld', 'l2_skipped']);

/**
 * Map a SectionProgress tier to a checklist marker status.
 *
 * `l2_withheld` and `l2_skipped` count as resolved: the questionnaire is
 * complete and the conversation was either score-gated away by the framework
 * or skipped by the user at the intermediary, so nothing is outstanding.
 * `l1_complete` is in progress — the questionnaire is answered and the
 * conversation is still owed. `declined` reads as not started, carrying no
 * depth: the user refused the section before answering it.
 *
 * Deliberately consistent with `sectionsExplored` in `TopicDetailView`, which
 * counts every tier except `none` and `declined`: exactly the set that renders
 * with a non-empty marker.
 *
 * @param {string|null|undefined} tier
 * @returns {'complete'|'in_progress'|'not_started'}
 */
export function statusForTier(tier) {
  if (RESOLVED_TIERS.has(tier)) return 'complete';
  if (tier === 'l1_complete') return 'in_progress';
  return 'not_started';
}
